/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigation } from 'react-router';
import type { Route } from './+types/_app';
import { usePageUIConfig, mainPaddingDataAttributes } from '@/lib/routes/page-ui-config';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { type ShopperProducts } from '@/scapi';
import { fetchCategory, fetchCategoriesByIds } from '@/lib/api/categories.server';
import { collectCategoryUrls } from '@/lib/product/seo-url';
import { CategoryHrefProvider, type CategoryUrlMap } from '@/components/navigation-menu/category-href';
import { getLogger } from '@/lib/logger.server';
import Header, { type PromoMessage } from '@/components/header';
import Footer from '@/components/footer';
import ResponsiveNavigationMenu from '@/components/navigation-menu-mega';
import NavSkeletonOverlay from '@/components/nav-skeleton-overlay';

import { WishlistMergeToast } from '@/components/wishlist/wishlist-merge-toast';
import { AuthConversionTracker } from '@/components/analytics/auth-conversion-tracker';
import { useAuth } from '@/providers/auth';
import { useWishlistSession } from '@/providers/wishlist';
import { SkipLink } from '@/components/skip-link';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';

/**
 * Slim progress bar at the very top of the viewport. Renders while React Router is
 * mid-navigation so the user gets immediate visual feedback that a click registered
 * during the (necessary) server round-trip on loader-heavy routes like the category
 * page. Auto-scales in on nav start, then *trickles* toward a ceiling (never freezes
 * at a fixed value while a slow loader runs), and finishes 100% + fades on completion.
 * Shown for every navigation — page-to-page clicks as well as same-page filter/param
 * changes — so it is the universal, route-agnostic navigation cue. On top-of-funnel
 * routes it pairs with <NavSkeletonOverlay>, which adds a destination-shaped skeleton
 * when a blocking loader outlasts the bar's trickle; all other routes rely on the bar
 * alone (see @/components/nav-skeleton-overlay for the funnel allowlist).
 */
function TopProgressBar() {
    const navigation = useNavigation();
    const isNavigating = navigation.state !== 'idle';
    const [visible, setVisible] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (isNavigating) {
            setVisible(true);
            // Small immediate jump so the bar is visible right away, then ease toward a
            // ~92% ceiling. Each tick adds a shrinking fraction of the remaining distance,
            // so the bar always keeps inching forward (never visually frozen) but never
            // reaches 100% on its own — completion is reserved for when the nav actually ends.
            setProgress(10);
            let current = 10;
            const id = setInterval(() => {
                current += Math.max(0.5, (92 - current) * 0.08);
                if (current > 92) current = 92;
                setProgress(current);
            }, 250);
            return () => clearInterval(id);
        }
        // Nav ended — complete then hide
        if (visible) {
            setProgress(100);
            const t = setTimeout(() => {
                setVisible(false);
                setProgress(0);
            }, 250);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [isNavigating, visible]);

    if (!visible) return null;
    return (
        <div
            aria-hidden="true"
            // RGBARRY-CUSTOMIZATION: keep the fixed progress bar below the iPhone tint/safe-area layer.
            className="fixed safe-area-progress-offset left-0 right-0 z-[60] h-0.5 pointer-events-none">
            <div
                className="h-full bg-foreground transition-all duration-300 ease-out"
                style={{ width: `${progress}%`, opacity: progress === 100 ? 0 : 1 }}
            />
        </div>
    );
}

type LoaderData = {
    root: Promise<ShopperProducts.schemas['Category']>;
    subs: Promise<ShopperProducts.schemas['Category'][]>;
    promoMessages: Promise<PromoMessage[]>;
    // RGBARRY-CUSTOMIZATION (W-23875749): categoryId -> production SEO slug path for mega-menu links.
    categoryUrls: Promise<CategoryUrlMap>;
};

// RGBARRY-CUSTOMIZATION: shown immediately on first paint while PD fetch streams in
const DEFAULT_PROMO_MESSAGES: PromoMessage[] = [
    { id: 'anti-theft', message: 'Shop Anti-Theft Bags — Travel With Confidence' },
];

/**
 * We're using the `shouldRevalidate` functionality to only load the navigation menu categories from the server on the
 * very first navigation to a page using the (default) `_app` layout. Those categories requests are divided into two
 * discrete request phases; the first to only fetch the root categories, and subsequent multiple calls to fetch the
 * identified sub categories to be displayed in the navigation menu. This behavior ensures that the initial data
 * doesn't get overwritten/removed on subsequent client-side navigations.
 *
 * **Note:** If there is a desire/intent to customize that behavior, the `shouldRevalidate` function can be modified
 * accordingly. For example, a client-side storage for the categories could be introduced to implement any desired
 * caching strategy, including specific reload/refresh intervals/conditions.
 * @see {@link https://reactrouter.com/start/framework/route-module#shouldrevalidate}
 */
export function shouldRevalidate() {
    return false;
}

export function loader({ context, request }: Route.LoaderArgs): LoaderData {
    const logger = getLogger(context);
    const config = getConfig(context);
    const { rootCategoryId, maxDepth } = config.pages.navigation;

    logger.debug('AppLayout: loader starting', { rootCategoryId, maxDepth });

    // Fetch root with enough depth to cover the full nav tree in one request.
    // This catalog's getCategories endpoint does not support batch ID lookups,
    // so we fetch the complete tree upfront via levels=maxDepth.
    const rootCategoryPromise = fetchCategory(context, rootCategoryId, maxDepth as 0 | 1 | 2);

    // Load each second-level sub categories tree as well, in case the resolved root-level category has any sub
    // categories and maxDepth allows for it. We then base this composed second-level promise on the initial root
    // category promise to allow for parallel loading and streaming of the two main promises. The sub category trees
    // are fetched in a single (chunked) getCategories request rather than one getCategory request per root category.
    const subCategoriesPromise =
        maxDepth >= 2
            ? rootCategoryPromise.then((rootCategory: ShopperProducts.schemas['Category']) => {
                  const subCategoryIds =
                      rootCategory.categories?.reduce((acc: string[], subCategory) => {
                          if (
                              typeof subCategory.onlineSubCategoriesCount === 'number' &&
                              subCategory.onlineSubCategoriesCount > 0
                          ) {
                              acc.push(subCategory.id);
                          }
                          return acc;
                      }, []) ?? [];

                  return fetchCategoriesByIds(context, subCategoryIds, maxDepth as 0 | 1 | 2);
              })
            : Promise.resolve([]);

    // RGBARRY-CUSTOMIZATION: fetch homepage Page Designer page to derive promo bar messages from
    // the 'main' region — same page/region editors manage in BM Page Designer.
    const homePagePromise = fetchPageWithComponentData({ context, request, params: {} } as Route.LoaderArgs, {
        pageId: 'header',
    });

    // Pluck AnnouncementBanner components from the header page's main region and map
    // them to the flat PromoMessage shape the promo bar needs.
    const promoMessagesPromise: Promise<PromoMessage[]> = homePagePromise.then((page) => {
        if (!page) return [];
        const headerBannerRegion = page.regions?.find((r) => r.id === 'main');
        if (!headerBannerRegion?.components?.length) return [];
        return headerBannerRegion.components
            .filter((c) => c.data && typeof (c.data as Record<string, unknown>).message === 'string')
            .map((c) => {
                const data = c.data as Record<string, unknown>;
                return {
                    id: c.id,
                    message: data.message as string,
                    linkUrl: typeof data.linkUrl === 'string' ? data.linkUrl : undefined,
                    couponCode: typeof data.couponCode === 'string' ? data.couponCode : undefined,
                };
            });
    });

    // RGBARRY-CUSTOMIZATION (W-23875749): project each mega-menu category's production SEO slug path
    // out of the `c_seoUrl` the int_urlresolver_scapi hooks stamp on the root category tree we ALREADY
    // fetch — no extra resolver round trip, and the enriched response is edge-cacheable. `collectCategoryUrls`
    // is a pure walk; a category whose hook produced no path is simply omitted, leaving the id-based
    // `/category/:id` fallback in place — no broken links, no 500s.
    //
    // Derived from `rootCategoryPromise` ALONE — deliberately NOT the deferred `subCategoriesPromise`.
    // The mega menu reads this via `use()` inside the same Suspense boundary that already awaits the root
    // tree, so chaining it off root keeps it in lockstep with that barrier; folding in the second
    // (deferred) sub-tree round trip would re-suspend the site-wide header nav on non-critical data
    // (CLAUDE.md Rules 3 & 6). The root fetch already covers L1/L2 to `maxDepth`; deeper L3 slugs present
    // only in the sub-trees fall back to the id route + 301 (one extra loader→301→loader hop on those
    // clicks) — an acceptable trade to keep the header off the deferred fetch's critical path.
    const categoryUrlsPromise = rootCategoryPromise
        .then((rootCategory) => collectCategoryUrls(rootCategory))
        .catch(() => ({}) as CategoryUrlMap);

    // RGBARRY-CUSTOMIZATION: passive rejection guards. These are deferred promises consumed downstream by
    // `<Await errorElement=…>` (ResponsiveNavigationMenu resolve/defer, Header's PromoBar). During streaming
    // SSR a rejection (e.g. a transient SCAPI 401 "invalid or revoked" on the shared nav category fetch) can
    // settle before React subscribes to the promise — which surfaces as an *unhandled* Node rejection and
    // takes down the whole dev server instead of degrading to the wired-up error UI. Attaching a no-op catch
    // to the same references (synchronously, at creation) marks them handled at the runtime level while
    // `<Await>` still receives the rejection and renders its errorElement. This layout wraps every `_app.*`
    // route, so an unguarded nav rejection crashes SSR site-wide. (`categoryUrlsPromise` already has a real
    // `.catch` fallback above, so it needs no guard.)
    void rootCategoryPromise.catch(() => undefined);
    void subCategoriesPromise.catch(() => undefined);
    void promoMessagesPromise.catch(() => undefined);

    return {
        root: rootCategoryPromise,
        subs: subCategoriesPromise,
        promoMessages: promoMessagesPromise,
        categoryUrls: categoryUrlsPromise,
    };
}

/**
 * Default Layout Route
 *
 * This pathless layout route provides the standard storefront UI structure:
 * - Header with navigation
 * - Main content area (via `<Outlet/>`)
 * - Footer
 *
 * Routes that need this layout should be prefixed with `_app.` in their filename.
 * For routes without default header/footer (e.g., login), use the `_empty.` prefix instead.
 */
export default function DefaultLayout({
    loaderData: { root, subs, promoMessages, categoryUrls },
}: {
    loaderData: LoaderData;
}) {
    const refRoot = useRef<Promise<ShopperProducts.schemas['Category']> | undefined>(undefined);
    const refSubs = useRef<Promise<ShopperProducts.schemas['Category'][]> | undefined>(undefined);
    const refPromoMessages = useRef<Promise<PromoMessage[]> | undefined>(undefined);
    const refCategoryUrls = useRef<Promise<CategoryUrlMap> | undefined>(undefined);
    if (!refRoot.current && !refSubs.current) {
        refRoot.current = root;
        refSubs.current = subs;
        refPromoMessages.current = promoMessages;
        refCategoryUrls.current = categoryUrls;
    }

    // RGBARRY-CUSTOMIZATION (W-23789029): resolve the category slug map client-side so the header's
    // search-suggestion category links resolve to prod-canonical SEO URLs. The dropdown is a
    // client-only typeahead (never SSR'd/crawled), so an empty map on first paint — which
    // `useCategoryHref` degrades to the id route + 301 — is harmless; it upgrades to slugs once
    // the already-in-flight nav promise settles.
    const [resolvedCategoryUrls, setResolvedCategoryUrls] = useState<CategoryUrlMap>({});
    useEffect(() => {
        let active = true;
        void refCategoryUrls.current
            ?.then((map) => {
                if (active) setResolvedCategoryUrls(map);
            })
            .catch(() => {
                // Slug map is best-effort; on failure the id-route + 301 fallback stands.
            });
        return () => {
            active = false;
        };
    }, []);

    // Reflect the route's `handle.ui` config onto <main> as data-* attributes
    // during render, so the correct top padding is present in the SSR'd HTML.
    // If your CSS keys <main> padding off these, emitting them at render (not
    // in a post-hydration effect) avoids a layout shift (CLS) when the padding
    // would otherwise be added after first paint. Inert if no CSS matches.
    const pageUIConfig = usePageUIConfig();
    const mainPaddingAttrs = mainPaddingDataAttributes(pageUIConfig);
    const mainBgClass = pageUIConfig.main?.backgroundClass ?? '';

    // Bind the module-level wishlist store (see providers/wishlist) to the current shopper here,
    // once, from client auth context. `customerId` only changes on login/logout — both are
    // redirects that revalidate the root loader, refreshing `useAuth()` — so the shell re-runs
    // this on every identity change and evicts the prior shopper's hearts. Plain browse never
    // changes it. The actual read stays lazy, fired by the first product intent.
    useWishlistSession(useAuth()?.customerId ?? null);

    // <WishlistMergeToast> stays at the app shell — it reads URL params and a one-time
    // cookie set by the post-login redirect target, not wishlist state.
    return (
        <>
            <SkipLink />
            {/* RGBARRY-CUSTOMIZATION: slim top progress bar + destination-shaped skeleton so a
                blocking navigation never leaves the shopper on a page that looks stuck */}
            <TopProgressBar />
            <NavSkeletonOverlay />
            <WishlistMergeToast />
            {/* RGBARRY-CUSTOMIZATION (W-23955510): fires GA4 sign_up/login off the post-auth URL flag. */}
            <AuthConversionTracker />
            {/* RGBARRY-CUSTOMIZATION: PD-driven promo bar; defaultPromoMessages shows on first paint
                while the PD fetch streams in — no blank bar, no CLS, no extra network request */}
            {/* RGBARRY-CUSTOMIZATION (W-23789029): provide the (client-resolved) category slug map to the
                header so search-suggestion category links emit prod-canonical SEO URLs. */}
            <CategoryHrefProvider value={resolvedCategoryUrls}>
                <Header promoMessages={refPromoMessages.current} defaultPromoMessages={DEFAULT_PROMO_MESSAGES}>
                    <ResponsiveNavigationMenu
                        resolve={refRoot.current}
                        defer={refSubs.current}
                        categoryUrls={refCategoryUrls.current}
                    />
                </Header>
            </CategoryHrefProvider>
            {/* RGBARRY-CUSTOMIZATION: mainBgClass from page UI config */}
            <main
                id="main-content"
                tabIndex={-1}
                className={`grow pt-8${mainBgClass ? ` ${mainBgClass}` : ''}`}
                {...mainPaddingAttrs}>
                <Outlet />
            </main>
            <Footer />
        </>
    );
}
